import { useEffect, useMemo, useRef, useState } from 'react'
import { ChevronDown, Info, Link2, Loader2, Play, Search, X } from 'lucide-react'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { ListPaginationBar } from '@/components/ui/ListPaginationBar'
import { PageHeader } from '@/features/invitee/components/PageHeader'
import { showApiError, showSuccess } from '@/lib/utils/notifications'
import { SortableHeader } from '@/features/yardi-matching/components/SortableHeader'
import { nextSortState, type SortDir } from '@/features/yardi-matching/utils/tableSort'
import { PmsAutomationTabs } from '../components/PmsAutomationTabs'
import { PropertyMappingActionDialog } from '../components/PropertyMappingActionDialog'
import { PropertyMappingReviewDialog } from '../components/PropertyMappingReviewDialog'
import { PmsAuditLogSection } from '../components/PmsAuditLogSection'
import {
  fetchPmcPropertyMappings,
  fetchPropertyMappingAudit,
  savePropertyMappingAuditNote,
  fetchPropertyMappingPmcs,
  runPropertyMatchingAutomation,
  updatePmcPropertyMappingStatus,
} from '../services/propertyMappingService'
import type {
  PmcPropertyMappingOption,
  PropertyMappingRow,
  PropertyMappingStatus,
} from '../types'

type StatusFilter = 'ALL' | 'MAPPED' | 'NEEDS_REVIEW' | 'SKIPPED' | 'UNMAPPED'

function statusBucket(status: PropertyMappingStatus): Exclude<StatusFilter, 'ALL'> {
  if (status === 'MAPPED') return 'MAPPED'
  if (status === 'SKIPPED') return 'SKIPPED'
  if (status === 'UNMAPPED') return 'UNMAPPED'
  return 'NEEDS_REVIEW'
}

const BUCKET_STYLES: Record<Exclude<StatusFilter, 'ALL'>, string> = {
  MAPPED: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  NEEDS_REVIEW: 'border-amber-200 bg-amber-50 text-amber-700',
  SKIPPED: 'border-slate-300 bg-slate-100 text-slate-700',
  UNMAPPED: 'border-rose-200 bg-rose-50 text-rose-700',
}

const BUCKET_LABELS: Record<Exclude<StatusFilter, 'ALL'>, string> = {
  MAPPED: 'Mapped',
  NEEDS_REVIEW: 'Needs review',
  SKIPPED: 'Skipped',
  UNMAPPED: 'Unmapped – No Yardi Property Found',
}

const STATUS_TILES: Array<{
  key: Exclude<StatusFilter, 'ALL'>
  label: string
  hint?: string
}> = [
  {
    key: 'MAPPED',
    label: 'All mapped',
    hint: 'Includes automatically and manually mapped properties',
  },
  { key: 'NEEDS_REVIEW', label: 'Needs review' },
  {
    key: 'UNMAPPED',
    label: 'Unmapped',
    hint: 'No Yardi property found',
  },
  { key: 'SKIPPED', label: 'Skipped' },
]

const TABLE_STATUS_LABELS: Record<Exclude<StatusFilter, 'ALL'>, string> = {
  MAPPED: 'Mapped',
  NEEDS_REVIEW: 'Needs review',
  SKIPPED: 'Skipped',
  UNMAPPED: 'Unmapped',
}

function StatusPill({ bucket }: { bucket: Exclude<StatusFilter, 'ALL'> }) {
  return (
    <span
      title={BUCKET_LABELS[bucket]}
      className={`inline-flex rounded-full border px-2.5 py-1 text-center text-xs font-semibold leading-4 ${
        BUCKET_STYLES[bucket]
      }`}
    >
      {TABLE_STATUS_LABELS[bucket]}
    </span>
  )
}

function MappingStatusBadge({
  status,
  alreadyMapped,
}: {
  status: PropertyMappingStatus
  alreadyMapped?: boolean
}) {
  if (status === 'NEEDS_REVIEW' && alreadyMapped) {
    return (
      <div className="flex flex-wrap gap-1.5">
        <StatusPill bucket="MAPPED" />
        <StatusPill bucket="NEEDS_REVIEW" />
      </div>
    )
  }

  return <StatusPill bucket={statusBucket(status)} />
}

const PER_PAGE_OPTIONS = [15, 30, 50, 100] as const

function MappingEnabledLabel({ enabled }: { enabled: boolean }) {
  return (
    <span
      className={`pmc-state inline-flex whitespace-nowrap rounded-[20px] px-[9px] py-1 text-xs font-bold ${
        enabled ? 'enabled bg-[#eefbf3] text-[#1b8f4a]' : 'disabled bg-[#fff0f1] text-[#d12c3e]'
      }`}
    >
      {enabled ? 'Enabled' : 'Disabled'}
    </span>
  )
}

function PmcSourceAccountSelect({
  pmcs,
  pmcId,
  loading,
  onChange,
}: {
  pmcs: PmcPropertyMappingOption[]
  pmcId: string
  loading: boolean
  onChange: (pmcId: string) => void
}) {
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const selected = pmcs.find((pmc) => pmc.pmcId === pmcId)

  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false)
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  return (
    <div ref={rootRef} className="relative mt-2">
      <button
        type="button"
        disabled={loading || pmcs.length === 0}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
        className="flex h-12 w-full items-center justify-between gap-4 rounded-full border border-slate-200 bg-white px-5 text-left outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100 disabled:bg-white"
      >
        <span className="min-w-0 truncate text-sm text-slate-800">
          {loading
            ? 'Loading PMCs…'
            : selected
              ? `${selected.companyName} · ${selected.propertyCount} properties`
              : 'No Yardi PMCs configured'}
        </span>
        <span className="flex shrink-0 items-center gap-2">
          {selected ? <MappingEnabledLabel enabled={selected.propertyMappingEnabled} /> : null}
          <ChevronDown className={`h-4 w-4 text-slate-400 transition ${open ? 'rotate-180' : ''}`} />
        </span>
      </button>
      {open ? (
        <ul
          role="listbox"
          className="absolute z-20 mt-2 max-h-72 w-full overflow-auto rounded-2xl border border-slate-200 bg-white py-1 shadow-lg"
        >
          {pmcs.map((pmc) => {
            const active = pmc.pmcId === pmcId
            return (
              <li key={pmc.pmcId}>
                <button
                  type="button"
                  role="option"
                  aria-selected={active}
                  onClick={() => {
                    onChange(pmc.pmcId)
                    setOpen(false)
                  }}
                  className={`flex w-full items-center justify-between gap-3 px-5 py-3 text-left hover:bg-slate-50 ${
                    active ? 'bg-slate-50' : ''
                  }`}
                >
                  <span className="min-w-0 truncate text-sm text-slate-800">
                    {pmc.companyName} · {pmc.propertyCount} properties
                  </span>
                  <MappingEnabledLabel enabled={pmc.propertyMappingEnabled} />
                </button>
              </li>
            )
          })}
        </ul>
      ) : null}
    </div>
  )
}

export function PmsPropertyMappingPage() {
  const [pmcs, setPmcs] = useState<PmcPropertyMappingOption[]>([])
  const [pmcId, setPmcId] = useState('')
  const [rows, setRows] = useState<PropertyMappingRow[]>([])
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState<StatusFilter>('ALL')
  const [sortKey, setSortKey] = useState('source_property_name')
  const [sortDir, setSortDir] = useState<SortDir>('asc')
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(15)
  const [total, setTotal] = useState(0)
  const [lastPage, setLastPage] = useState(1)
  const [loadingPmcs, setLoadingPmcs] = useState(true)
  const [loadingProperties, setLoadingProperties] = useState(false)
  const [savingPmcId, setSavingPmcId] = useState<string | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [running, setRunning] = useState(false)
  const [reloadToken, setReloadToken] = useState(0)
  const [reviewRow, setReviewRow] = useState<PropertyMappingRow | null>(null)
  const [statusRow, setStatusRow] = useState<PropertyMappingRow | null>(null)
  const [confirmAutomation, setConfirmAutomation] = useState(false)
  const [statusCounts, setStatusCounts] = useState<Record<StatusFilter, number>>({
    ALL: 0,
    MAPPED: 0,
    NEEDS_REVIEW: 0,
    SKIPPED: 0,
    UNMAPPED: 0,
  })
  const propertyRequestId = useRef(0)
  const searchTimer = useRef<number | null>(null)

  const selectedPmc = useMemo(
    () => pmcs.find((pmc) => pmc.pmcId === pmcId),
    [pmcs, pmcId],
  )
  const enabledPmcCount = pmcs.filter((pmc) => pmc.propertyMappingEnabled).length
  const hasFilters = Boolean(search.trim()) || status !== 'ALL'
  const selectedTile = STATUS_TILES.find((tile) => tile.key === status)

  const changeSort = (field: string) => {
    const next = nextSortState(sortKey, sortDir, field)
    setSortKey(next.key)
    setSortDir(next.dir)
    setPage(1)
  }

  useEffect(() => {
    setLoadingPmcs(true)
    setLoadError(null)
    void fetchPropertyMappingPmcs()
      .then((result) => {
        setPmcs(result)
        setPmcId((current) =>
          current && result.some((pmc) => pmc.pmcId === current)
            ? current
            : result[0]?.pmcId || '',
        )
      })
      .catch((error) => {
        setLoadError('Could not load Yardi PMC configurations.')
        showApiError(error)
      })
      .finally(() => setLoadingPmcs(false))
  }, [])

  useEffect(() => {
    if (!pmcId) {
      setRows([])
      setTotal(0)
      setLastPage(1)
      setStatusCounts({ ALL: 0, MAPPED: 0, NEEDS_REVIEW: 0, SKIPPED: 0, UNMAPPED: 0 })
      return
    }

    if (searchTimer.current) window.clearTimeout(searchTimer.current)
    const requestId = ++propertyRequestId.current
    const delay = search.trim() ? 350 : 0
    searchTimer.current = window.setTimeout(() => {
      setLoadingProperties(true)
      setLoadError(null)
      void fetchPmcPropertyMappings({
        pmc_id: pmcId,
        search: search.trim() || undefined,
        status: status === 'ALL' ? undefined : status,
        page,
        perPage,
        orderBy: sortKey,
        sortBy: sortDir,
      })
        .then((result) => {
          if (requestId !== propertyRequestId.current) return
          setRows(result.items)
          setTotal(result.total)
          setLastPage(result.last)
          setPage(result.current)
          setStatusCounts({
            ALL:
              result.statusCounts.MAPPED +
              result.statusCounts.NEEDS_REVIEW +
              result.statusCounts.SKIPPED +
              result.statusCounts.UNMAPPED,
            MAPPED: result.statusCounts.MAPPED,
            NEEDS_REVIEW: result.statusCounts.NEEDS_REVIEW,
            SKIPPED: result.statusCounts.SKIPPED,
            UNMAPPED: result.statusCounts.UNMAPPED,
          })
        })
        .catch((error) => {
          if (requestId !== propertyRequestId.current) return
          setRows([])
          setTotal(0)
          setLastPage(1)
          setLoadError('Could not load properties for the selected PMC.')
          showApiError(error)
        })
        .finally(() => {
          if (requestId === propertyRequestId.current) setLoadingProperties(false)
        })
    }, delay)

    return () => {
      if (searchTimer.current) window.clearTimeout(searchTimer.current)
    }
  }, [pmcId, reloadToken, search, status, page, perPage, sortKey, sortDir])

  const toggleSelectedPmc = async () => {
    if (!selectedPmc || savingPmcId) return
    const enabled = !selectedPmc.propertyMappingEnabled
    setSavingPmcId(selectedPmc.pmcId)
    try {
      await updatePmcPropertyMappingStatus(selectedPmc.pmcId, enabled)
      setPmcs((current) =>
        current.map((pmc) =>
          pmc.pmcId === selectedPmc.pmcId
            ? { ...pmc, propertyMappingEnabled: enabled }
            : pmc,
        ),
      )
      showSuccess(
        `Property matching ${enabled ? 'enabled' : 'disabled'} for ${selectedPmc.companyName}.`,
      )
      setReloadToken((value) => value + 1)
    } catch (error) {
      showApiError(error)
    } finally {
      setSavingPmcId(null)
    }
  }

  const runAutomation = async () => {
    if (!enabledPmcCount || running) return
    setRunning(true)
    try {
      const result = await runPropertyMatchingAutomation()
      showSuccess(
        `Processed ${result.propertiesProcessed} properties across ${result.pmcsProcessed} enabled PMCs: ` +
          `${result.autoMapped} mapped, ${result.needsReview} need review, ${result.unmapped} unmapped.`,
      )
      setReloadToken((value) => value + 1)
    } catch (error) {
      showApiError(error)
    } finally {
      setRunning(false)
    }
  }

  return (
    <div className="min-h-full bg-[#f5f7fb]" data-testid="pms-property-mapping-page">
      <PageHeader title="PMS Automation" testIdRoot="pms-property-mapping-header" />

      <div className="space-y-5 p-4 sm:p-6">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-slate-900">
              Yardi Property Mapping
            </h1>
            <p className="mt-1 max-w-3xl text-sm text-slate-500">
              Manage PMC automation and review property relationships.
            </p>
          </div>
          <button
            type="button"
            disabled={!enabledPmcCount || running}
            title={
              enabledPmcCount
                ? 'Match properties for every enabled PMC'
                : 'Enable at least one PMC before running automation'
            }
            onClick={() => void runAutomation()}
            className="inline-flex h-10 items-center gap-2 self-start rounded-lg bg-sky-500 px-4 text-sm font-semibold text-white shadow-sm disabled:cursor-not-allowed disabled:opacity-60"
          >
            {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
            Run Automation
          </button>
        </div>

        <PmsAutomationTabs />

        <div className="rounded-2xl border border-slate-200 bg-white px-5 py-4 shadow-sm">
          <div className="grid gap-5 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)] lg:items-center">
            <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-800">
              Select PMC / source account
              <PmcSourceAccountSelect
                pmcs={pmcs}
                pmcId={pmcId}
                loading={loadingPmcs}
                onChange={(nextPmcId) => {
                  setPmcId(nextPmcId)
                  setPage(1)
                }}
              />
            </div>

            <div className="flex items-center justify-between gap-4">
              <div className="min-w-0">
                <div className="text-[15px] font-semibold text-slate-800">
                  Automation for {selectedPmc?.companyName || 'selected PMC'}
                </div>
                <div className="mt-0.5 text-sm">
                  <span
                    className={
                      selectedPmc?.propertyMappingEnabled ? 'text-emerald-500' : 'text-[#f43f5e]'
                    }
                  >
                    {selectedPmc?.propertyMappingEnabled ? 'Enabled' : 'Disabled'}
                  </span>
                  <span className="text-slate-400">
                    {selectedPmc?.propertyMappingEnabled
                      ? ' · Included in the next run'
                      : ' · Excluded from next run'}
                  </span>
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-3">
                <button
                  type="button"
                  role="switch"
                  aria-checked={Boolean(selectedPmc?.propertyMappingEnabled)}
                  aria-label="Enable property automation for selected PMC"
                  disabled={!selectedPmc || savingPmcId === selectedPmc.pmcId}
                  onClick={() => setConfirmAutomation(true)}
                  className={`relative h-7 w-12 rounded-full transition disabled:cursor-not-allowed ${
                    selectedPmc?.propertyMappingEnabled ? 'bg-emerald-500' : 'bg-slate-200'
                  }`}
                >
                  <span
                    className={`absolute top-0.5 h-6 w-6 rounded-full bg-white shadow-sm transition ${
                      selectedPmc?.propertyMappingEnabled ? 'left-[22px]' : 'left-0.5'
                    }`}
                  />
                </button>
                <span className="whitespace-nowrap text-xs text-slate-400">
                  {savingPmcId === selectedPmc?.pmcId ? (
                    <Loader2 className="mr-1 inline h-3.5 w-3.5 animate-spin" />
                  ) : null}
                  {enabledPmcCount} / {pmcs.length} PMCs enabled
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="text-[11px] font-bold uppercase tracking-[0.12em] text-slate-800">
            Property status overview
          </div>
          <p className="mt-1 text-xs text-slate-500">
            Select a tile to filter by Mapped, Needs review, Unmapped, or Skipped.
          </p>
          <div className="mt-3 grid grid-cols-2 gap-3 xl:grid-cols-4">
            {STATUS_TILES.map((tile) => {
              const selected = status === tile.key
              return (
                <button
                  key={tile.key}
                  type="button"
                  onClick={() => {
                    setStatus(tile.key)
                    setPage(1)
                  }}
                  className={`rounded-xl border bg-white px-3 py-3 text-left shadow-sm transition ${
                    selected
                      ? 'border-sky-400 ring-2 ring-sky-100'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <div className="flex items-center gap-1 text-xs font-medium text-slate-500">
                    <span>{tile.label}</span>
                    {tile.hint ? (
                      <span title={tile.hint} className="inline-flex text-slate-400">
                        <Info className="h-3.5 w-3.5" />
                      </span>
                    ) : null}
                  </div>
                  <div className="mt-1 text-2xl font-bold text-slate-900">
                    {loadingProperties ? '—' : statusCounts[tile.key]}
                  </div>
                </button>
              )
            })}
          </div>
        </div>

        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-200 p-4">
            <label className="text-[11px] font-bold uppercase tracking-[0.12em] text-slate-800">
              Search properties
              <div className="relative mt-1">
                <Search className="pointer-events-none absolute left-3 top-3.5 h-4 w-4 text-slate-400" />
                <input
                  value={search}
                  onChange={(event) => {
                    setSearch(event.target.value)
                    setPage(1)
                  }}
                  placeholder="Search Yardi code, property name or address"
                  className="h-11 w-full rounded-lg border border-slate-300 bg-white pl-9 pr-3 text-sm outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-100"
                />
              </div>
            </label>
            <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500">
              <span>
                Showing {loadingProperties ? '—' : rows.length} of {total}
                {selectedTile && status !== 'ALL' ? ` · ${selectedTile.label}` : ''}
              </span>
              {hasFilters ? (
                <button
                  type="button"
                  onClick={() => {
                    setSearch('')
                    setStatus('ALL')
                    setPage(1)
                  }}
                  className="inline-flex items-center gap-1 font-semibold text-sky-600 hover:text-sky-700"
                >
                  <X className="h-3.5 w-3.5" />
                  Clear filters
                </button>
              ) : null}
            </div>
          </div>

          {loadError ? (
            <div className="border-b border-rose-100 bg-rose-50 px-4 py-3 text-sm text-rose-700">
              {loadError}
            </div>
          ) : null}

          <ListPaginationBar
            placement="header"
            currentPage={page}
            lastPage={lastPage}
            totalRecords={total}
            perPage={perPage}
            onPageChange={setPage}
            onPerPageChange={(next) => {
              setPerPage(next)
              setPage(1)
            }}
            perPageOptions={PER_PAGE_OPTIONS}
            testIdPrefix="pms-property-mapping"
            barDataTestId="pms-property-mapping-pagination-header-bar"
          />
          <div>
            <table className="w-full table-fixed text-left text-sm">
              <colgroup>
                <col className="w-[15%]" />
                <col className="w-[11%]" />
                <col className="w-[15%]" />
                <col className="w-[9%]" />
                <col className="w-[8%]" />
                <col className="w-[12%]" />
                <col className="w-[10%]" />
                <col className="w-[20%]" />
              </colgroup>
              <thead className="bg-slate-50 text-[11px] font-semibold uppercase leading-4 tracking-wide text-slate-500">
                <tr>
                  <SortableHeader label="Liberty Rent property" field="liberty_property_name" sortKey={sortKey} sortDir={sortDir} onSort={changeSort} />
                  <SortableHeader label="Yardi code" field="source_property_code" sortKey={sortKey} sortDir={sortDir} onSort={changeSort} />
                  <SortableHeader label="Yardi property" field="source_property_name" sortKey={sortKey} sortDir={sortDir} onSort={changeSort} />
                  <SortableHeader label="Status" field="status" sortKey={sortKey} sortDir={sortDir} onSort={changeSort} />
                  <SortableHeader label="Match score" field="confidence" sortKey={sortKey} sortDir={sortDir} onSort={changeSort} />
                  <SortableHeader label="Match result" field="match_reason" sortKey={sortKey} sortDir={sortDir} onSort={changeSort} />
                  <SortableHeader label="Last checked" field="last_checked_at" sortKey={sortKey} sortDir={sortDir} onSort={changeSort} />
                  <th className="px-3 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loadingProperties ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-14 text-center text-slate-500">
                      <Loader2 className="mr-2 inline h-4 w-4 animate-spin" />
                      Loading properties…
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-14 text-center">
                      <Link2 className="mx-auto h-8 w-8 text-slate-300" />
                      <div className="mt-2 font-semibold text-slate-700">No properties found</div>
                      <div className="mt-1 text-sm text-slate-500">
                        Try another PMC, status tile, or search term.
                      </div>
                    </td>
                  </tr>
                ) : (
                  rows.map((row) => (
                    <tr key={row.id} className="hover:bg-slate-50/70">
                      <td className="px-3 py-4 align-top">
                        {row.libertyPropertyName ? (
                          <>
                            <div className="break-words font-semibold text-slate-800">{row.libertyPropertyName}</div>
                            <div className="mt-1 break-words text-xs leading-5 text-slate-500">
                              {row.libertyAddress}
                            </div>
                          </>
                        ) : (
                          <div>
                            <div className="font-semibold text-slate-400">Unmapped</div>
                            <div className="mt-1 text-xs text-slate-400">
                              No Liberty Rent property assigned
                            </div>
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-4 align-top">
                        <span className="inline-block max-w-full break-all rounded-md bg-slate-100 px-2 py-1 font-mono text-xs font-semibold text-slate-700">
                          {row.sourcePropertyCode}
                        </span>
                      </td>
                      <td className="px-3 py-4 align-top">
                        <div className="break-words font-semibold text-slate-900">{row.sourcePropertyName}</div>
                        {row.sourceAddress ? (
                          <div className="mt-1 break-words text-xs leading-5 text-slate-500">{row.sourceAddress}</div>
                        ) : null}
                      </td>
                      <td className="px-3 py-4 align-top">
                        <MappingStatusBadge status={row.status} alreadyMapped={row.alreadyMapped} />
                      </td>
                      <td className="px-3 py-4 align-top">
                        {row.confidence != null ? (
                          <div className="w-full max-w-[96px]">
                            <div className="text-sm font-semibold text-slate-800">
                              {row.confidence}/160
                            </div>
                            <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-slate-100">
                              <div
                                className="h-full rounded-full bg-gradient-to-r from-[#27b36a] to-[#59cf86]"
                                style={{ width: `${Math.min((row.confidence / 160) * 100, 100)}%` }}
                              />
                            </div>
                          </div>
                        ) : (
                          <span className="text-xs text-slate-400">—</span>
                        )}
                      </td>
                      <td className="px-3 py-4 align-top text-xs leading-5 text-slate-600">
                        <span className="break-words">{row.matchReason || 'Not checked yet'}</span>
                      </td>
                      <td className="px-3 py-4 align-top text-xs leading-5 text-slate-500">
                        {row.lastCheckedAt}
                      </td>
                      <td className="px-3 py-4 align-top">
                        <div className="flex justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => setReviewRow(row)}
                            className="h-8 whitespace-nowrap rounded-lg border border-slate-300 bg-white px-3 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                          >
                            Review
                          </button>
                          <button
                            type="button"
                            onClick={() => setStatusRow(row)}
                            className="h-8 whitespace-nowrap rounded-lg bg-sky-500 px-3 text-xs font-semibold text-white hover:bg-sky-600"
                          >
                            Update Status
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <ListPaginationBar
            placement="footer"
            currentPage={page}
            lastPage={lastPage}
            totalRecords={total}
            perPage={perPage}
            onPageChange={setPage}
            onPerPageChange={(next) => {
              setPerPage(next)
              setPage(1)
            }}
            perPageOptions={PER_PAGE_OPTIONS}
            testIdPrefix="pms-property-mapping"
            barDataTestId="pms-property-mapping-pagination-footer-bar"
          />
        </div>

        <PmsAuditLogSection
          title="Audit Logs"
          testId="pms-property-mapping-audit"
          reloadKey={`${pmcId}:${reloadToken}`}
          loadPage={async (page, perPage) => {
            if (!pmcId) {
              return { rows: [], total: 0, page, perPage, lastPage: 1 }
            }
            return fetchPropertyMappingAudit({ pmc_id: pmcId, page, per_page: perPage })
          }}
          onSave={
            pmcId
              ? async (note) => {
                  await savePropertyMappingAuditNote({ note, pmc_id: pmcId })
                }
              : undefined
          }
        />
      </div>

      <ConfirmDialog
        isOpen={confirmAutomation}
        title="Property Mapping Automation"
        message={
          selectedPmc
            ? `Are you sure you want to ${selectedPmc.propertyMappingEnabled ? 'disable' : 'enable'} property mapping automation for ${selectedPmc.companyName}?`
            : ''
        }
        primaryLabel="Confirm"
        secondaryLabel="Cancel"
        presentation="saveChanges"
        overlayDataTestId="pms-property-mapping-automation-confirm"
        primaryButtonDataTestId="pms-property-mapping-automation-confirm-yes"
        secondaryButtonDataTestId="pms-property-mapping-automation-confirm-no"
        onPrimary={() => {
          setConfirmAutomation(false)
          void toggleSelectedPmc()
        }}
        onSecondary={() => setConfirmAutomation(false)}
        onDismiss={() => setConfirmAutomation(false)}
      />
      <PropertyMappingReviewDialog row={reviewRow} onClose={() => setReviewRow(null)} />
      <PropertyMappingActionDialog
        row={statusRow}
        onClose={() => setStatusRow(null)}
        onSaved={() => {
          setStatusRow(null)
          setReloadToken((value) => value + 1)
        }}
      />
    </div>
  )
}
